'use client'
import { API_URL } from '@/config';
import React, { useState, useEffect, Suspense } from 'react';
import { Input } from '@/components/ui/input';
import { useRouter } from 'next/navigation';
import { useToast } from '@/components/hooks/use-toast';
import { EyeIcon } from '@heroicons/react/24/outline';
import { useGoogleLogin } from '@react-oauth/google';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import LoadingScreen from '@/components/LoadingScreen';
import AnimatedButton from '@/components/ui/animated-button';

const EmailVerificationDialog = () => {
    const searchParams = useSearchParams();
    const emailVerified = searchParams.get('email_verified');
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (emailVerified === 'true') {
            setOpen(true);
            if (typeof window !== "undefined" && window?.datafast) {
                window.datafast("email_verified", {
                    description: "The user has verified their email address.",
                });
            }
        }
    }, [emailVerified]);

    return (
        <Dialog open={open} onOpenChange={(e) => setOpen(e)}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className='text-center'>Email Verification Successful</DialogTitle>
                    <DialogDescription className='text-center'>
                        Your email has been verified. Please login to continue.
                    </DialogDescription>
                </DialogHeader>
            </DialogContent>
        </Dialog>
    );
};

const LoginForm = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const router = useRouter();
    const { toast } = useToast();
    const searchParams = useSearchParams();
    const nextTo = searchParams.get('nextto');

    // Validate redirect path to prevent open redirects
    const getValidRedirectPath = (path) => {
        if (!path) return '/dashboard';
        
        try {
            const decoded = decodeURIComponent(path);
            // Only allow relative paths starting with /
            // Reject external URLs, javascript:, data:, etc.
            if (decoded.startsWith('/') && !decoded.match(/^https?:\/\//i) && !decoded.match(/^javascript:/i) && !decoded.match(/^data:/i)) {
                return decoded;
            }
        } catch (e) {
            console.error('Invalid redirect path:', e);
        }
        
        return '/dashboard';
    };

    const googleLogin = useGoogleLogin({
        redirect_uri: `${API_URL}/accounts/google/login/callback/`,
        onSuccess: async (tokenResponse) => {
            try {
                let headers = {
                    'Content-Type': 'application/json',
                }
                const response = await fetch('/api/google-callback', {
                    method: 'POST',
                    headers: headers,
                    body: JSON.stringify({
                        code: tokenResponse.code,
                    }),
                    credentials: 'include',
                });
                const data = await response.json();
                if (!response.ok) {
                    if (data.non_field_errors) {
                        toast({
                            title: 'Error',
                            description: data.non_field_errors[0],
                            variant: 'destructive'
                        })
                    } else {
                        for (const key in data) {
                            toast({
                                title: 'Error',
                                description: data[key][0],
                                variant: 'destructive'
                            })
                        }
                    }
                    return
                }
            

                const enhancedData = {};
                if (data.user?.email) {
                    enhancedData.email = data.user.email;
                }
                if (data.user?.first_name) {
                    enhancedData.first_name = data.user.first_name;
                }
                if (data.user?.last_name) {
                    enhancedData.last_name = data.user.last_name;
                }
                if (typeof window !== "undefined" && window?.datafast) {
                    window.datafast("user_login_google", {
                        description: "The user has logged in with Google.",
                    });
                }
                // Redirect to nextto if provided, otherwise default to dashboard
                const redirectPath = getValidRedirectPath(nextTo);
                router.push(redirectPath)

            } catch (err) {
                console.error('Login error:', err);
            }
        },
        onError: (errorResponse) => {
            setError('Google login failed: ' + errorResponse.error_description);
        },
        flow: 'auth-code',
        scope: 'email profile',
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        let res = await fetch(`/api/login`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ email: email, password: password })
        })
        if (res.ok) {
            // Add TikTok pixel tracking
            if (typeof window !== 'undefined' && window.ttq) {
                const hashedEmail = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(email))
                    .then(hash => Array.from(new Uint8Array(hash))
                        .map(b => b.toString(16).padStart(2, '0'))
                        .join(''));
                
                window.ttq.identify({
                    email: hashedEmail
                });
            }
            if (typeof window !== "undefined" && window?.datafast) {
                console.log("datafast")
                window.datafast("user_login", {
                    description: "The user has logged in.",
                });
            }
            // Redirect to nextto if provided, otherwise default to dashboard
            const redirectPath = getValidRedirectPath(nextTo);
            router.push(redirectPath)
        } else {
            const data = await res.json();
            for (const key in data.error) {
                toast({
                    title: 'Error',
                    description: data.error[key][0],
                    variant: 'destructive'
                })
            }
        }
    }

    return (
        <>
            <Suspense fallback={null}>
                <EmailVerificationDialog />
            </Suspense>
            <h1 className="mt-10 text-3xl font-bold text-center lg:mt-20 xl:mt-32 sm:text-4xl xl:text-5xl font-pj lg:text-left text-white lg:text-gray-900 dark:lg:text-foreground">Login to your account</h1>
            <form className="mt-10" onSubmit={handleSubmit}>
                <div className="space-y-4">
                    <div>
                        <label className="sr-only"> Email</label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                                <svg className="w-5 h-5 text-gray-400 dark:text-muted-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <Input type="text" name="" id="" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="block w-full py-6 pl-12 pr-4 overflow-hidden text-base font-normal text-gray-900 dark:text-foreground placeholder-gray-600 dark:placeholder-muted-foreground transition-all duration-200 border border-gray-300 dark:border-input caret-gray-900 dark:caret-foreground rounded-xl bg-gray-50 dark:bg-background focus:outline-none focus:bg-white dark:focus:bg-background focus:border-gray-900 dark:focus:border-ring focus:ring-gray-900 dark:focus:ring-ring font-pj" />
                        </div>
                    </div>

                    <div>
                        <label className="sr-only"> Password </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                                <svg className="w-5 h-5 text-gray-400 dark:text-muted-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>

                            <Input type={showPassword ? 'text' : 'password'} name="" id="" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="block w-full py-6 pl-12 pr-4 overflow-hidden text-base font-normal text-gray-900 dark:text-foreground placeholder-gray-600 dark:placeholder-muted-foreground transition-all duration-200 border border-gray-300 dark:border-input caret-gray-900 dark:caret-foreground rounded-xl bg-gray-50 dark:bg-background focus:outline-none focus:bg-white dark:focus:bg-background focus:border-gray-900 dark:focus:border-ring focus:ring-gray-900 dark:focus:ring-ring font-pj" />
                            <div className='absolute inset-y-0 right-0 flex items-center pr-4 cursor-pointer' onClick={() => setShowPassword(!showPassword)}>
                                <EyeIcon className='w-5 h-5 text-gray-400 dark:text-muted-foreground' />
                            </div>
                        </div>
                    </div>
                    <div className='flex justify-end'>
                        <Link href="/password-reset" className='text-sm font-pj hover:underline text-white lg:text-gray-900 dark:lg:text-foreground'>Forgot password?</Link>
                    </div>
                </div>

                <div className="relative mt-8">
                    <AnimatedButton type="submit">
                        Login
                    </AnimatedButton>
                </div>

                <button
                    onClick={(e) => {
                        e.preventDefault()
                        googleLogin()
                    }}
                    className="
                        flex
                        items-center
                        justify-center
                        w-full
                        px-8
                        py-4
                        mt-8
                        text-base
                        font-bold
                        text-gray-900
                        dark:text-foreground
                        transition-all
                        duration-200
                        bg-gray-100
                        dark:bg-secondary
                        border border-transparent
                        dark:border-border
                        rounded-xl
                        hover:bg-gray-200 dark:hover:bg-secondary/80 focus:bg-gray-200 dark:focus:bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 dark:focus:ring-ring font-pj" role="button">
                    <svg className="w-5 h-5 mr-4" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M19.2436 8.26113L11.0858 8.26074C10.7256 8.26074 10.4336 8.5527 10.4336 8.91293V11.519C10.4336 11.8791 10.7256 12.1712 11.0858 12.1712H15.6798C15.1767 13.4767 14.2378 14.57 13.0399 15.2647L14.9988 18.6557C18.1411 16.8384 19.9988 13.6497 19.9988 10.0803C19.9988 9.57203 19.9613 9.20871 19.8864 8.79961C19.8295 8.48879 19.5596 8.26113 19.2436 8.26113Z"
                            fill="#167EE6"
                        />
                        <path
                            d="M9.99957 16.0871C7.75137 16.0871 5.78871 14.8587 4.73461 13.041L1.34375 14.9955C3.06934 17.9862 6.30191 20.0001 9.99957 20.0001C11.8135 20.0001 13.5251 19.5117 14.9996 18.6606V18.6559L13.0407 15.2649C12.1447 15.7846 11.1078 16.0871 9.99957 16.0871Z"
                            fill="#12B347"
                        />
                        <path d="M15 18.6603V18.6557L13.0411 15.2646C12.1451 15.7843 11.1083 16.0868 10 16.0868V19.9998C11.8139 19.9998 13.5256 19.5114 15 18.6603Z" fill="#0F993E" />
                        <path d="M3.91305 10.0002C3.91305 8.89207 4.21547 7.85531 4.73504 6.95934L1.34418 5.00488C0.488359 6.47469 0 8.18164 0 10.0002C0 11.8188 0.488359 13.5258 1.34418 14.9956L4.73504 13.0411C4.21547 12.1452 3.91305 11.1084 3.91305 10.0002Z" fill="#FFD500" />
                        <path
                            d="M9.99957 3.91305C11.4656 3.91305 12.8123 4.43398 13.8641 5.30051C14.1236 5.51426 14.5007 5.49883 14.7384 5.26113L16.5849 3.41465C16.8546 3.14496 16.8354 2.70352 16.5473 2.45359C14.785 0.924726 12.492 0 9.99957 0C6.30191 0 3.06934 2.01395 1.34375 5.00465L4.73461 6.9591C5.78871 5.14141 7.75137 3.91305 9.99957 3.91305Z"
                            fill="#FF4B26"
                        />
                        <path d="M13.8645 5.30051C14.124 5.51426 14.5012 5.49883 14.7389 5.26113L16.5854 3.41465C16.855 3.14496 16.8358 2.70352 16.5477 2.45359C14.7854 0.924688 12.4925 0 10 0V3.91305C11.466 3.91305 12.8127 4.43398 13.8645 5.30051Z" fill="#D93F21" />
                    </svg>
                    Sign in with Google
                </button>
            </form>

            <p className="mt-10 text-base font-normal text-center lg:mt-20 xl:mt-32 font-pj lg:text-left text-white lg:text-gray-900 dark:lg:text-foreground">Don't have an account? <Link href="/signup" title="" className="font-bold rounded focus:outline-none focus:ring-1 focus:ring-gray-900 dark:focus:ring-ring focus:ring-offset-2 hover:underline">Sign up now</Link></p>
        </>
    )
}

const Page = () => {
    return (
        <Suspense fallback={<LoadingScreen />}>
            <LoginForm />
        </Suspense>
    );
}

export default Page;
